/**
 * Searchable MCC code selector with risk level indicators.
 *
 * Groups MCC codes by category and shows a colored risk badge next to each
 * code. Supports keyboard navigation and search filtering.
 *
 * @module components/MccSelector
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ---------------------------------------------------------------------------
// MCC data (inlined to avoid a package dependency in the portal bundle)
// ---------------------------------------------------------------------------

interface MccEntry {
  code: string;
  description: string;
  riskLevel: 'low' | 'medium' | 'high' | 'prohibited';
}

const MCC_CODES: MccEntry[] = [
  { code: '5411', description: 'Grocery Stores, Supermarkets', riskLevel: 'low' },
  { code: '5412', description: 'Convenience Stores', riskLevel: 'low' },
  { code: '5541', description: 'Service Stations (Gasoline)', riskLevel: 'low' },
  { code: '5812', description: 'Eating Places, Restaurants', riskLevel: 'low' },
  { code: '5813', description: 'Drinking Places (Bars, Taverns)', riskLevel: 'low' },
  { code: '5311', description: 'Department Stores', riskLevel: 'low' },
  { code: '5331', description: 'Variety Stores', riskLevel: 'low' },
  { code: '5651', description: 'Family Clothing Stores', riskLevel: 'low' },
  { code: '5912', description: 'Drug Stores, Pharmacies', riskLevel: 'low' },
  { code: '5942', description: 'Book Stores', riskLevel: 'low' },
  { code: '5944', description: 'Jewelry, Watch, Clock Shops', riskLevel: 'low' },
  { code: '7011', description: 'Hotels, Motels, Resorts', riskLevel: 'low' },
  { code: '7230', description: 'Beauty and Barber Shops', riskLevel: 'low' },
  { code: '8011', description: 'Doctors (Not Elsewhere Classified)', riskLevel: 'low' },
  { code: '8021', description: 'Dentists, Orthodontists', riskLevel: 'low' },
  { code: '5734', description: 'Computer Software Stores', riskLevel: 'medium' },
  { code: '5816', description: 'Digital Goods — Games', riskLevel: 'medium' },
  { code: '5817', description: 'Digital Goods — Applications', riskLevel: 'medium' },
  { code: '5818', description: 'Digital Goods — Large Volume', riskLevel: 'medium' },
  { code: '4816', description: 'Computer Network / Information Services', riskLevel: 'medium' },
  { code: '5964', description: 'Direct Marketing — Catalog Merchant', riskLevel: 'medium' },
  { code: '5966', description: 'Direct Marketing — Outbound Telemarketing', riskLevel: 'medium' },
  { code: '5968', description: 'Direct Marketing — Subscription', riskLevel: 'medium' },
  { code: '7372', description: 'Computer Programming, Data Processing', riskLevel: 'medium' },
  { code: '7399', description: 'Business Services — Not Elsewhere Classified', riskLevel: 'medium' },
  { code: '7995', description: 'Betting / Casino Gambling', riskLevel: 'high' },
  { code: '5933', description: 'Pawn Shops', riskLevel: 'high' },
  { code: '6010', description: 'Financial Institutions — Manual Cash', riskLevel: 'high' },
  { code: '6051', description: 'Non-Financial Institutions — Foreign Currency / Crypto', riskLevel: 'high' },
  { code: '6211', description: 'Security Brokers / Dealers', riskLevel: 'high' },
  { code: '4829', description: 'Wire Transfer / Money Orders', riskLevel: 'high' },
  { code: '7801', description: 'Government-Licensed Online Casinos', riskLevel: 'high' },
  { code: '7273', description: 'Dating Services', riskLevel: 'high' },
];

// ---------------------------------------------------------------------------
// Risk level badge colors
// ---------------------------------------------------------------------------

const RISK_COLORS: Record<string, string> = {
  low: 'bg-primary-tint text-primary-hover',
  medium: 'bg-warning-tint text-warning',
  high: 'bg-danger-tint text-danger',
  prohibited: 'bg-ink-surface text-white',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface MccSelectorProps {
  /** Currently selected MCC code, or null. */
  value: string | null;
  /** Called when user selects an MCC code. */
  onChange: (mccCode: string) => void;
  /** Optional error message. */
  error?: string | null;
  /** Whether the selector is disabled. */
  disabled?: boolean;
}

export function MccSelector({
  value,
  onChange,
  error,
  disabled = false,
}: MccSelectorProps) {
  const { t } = useTranslation('portal');
  const riskLabel = (level: string) =>
    t(`mccSelector.riskLevels.${level}`, { defaultValue: level });
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredCodes = useMemo(() => {
    if (!search.trim()) return MCC_CODES;
    const q = search.toLowerCase();
    return MCC_CODES.filter(
      (entry) =>
        entry.code.includes(q) ||
        entry.description.toLowerCase().includes(q),
    );
  }, [search]);

  // Group by risk level for display
  const grouped = useMemo(() => {
    const groups: Record<string, MccEntry[]> = {
      low: [],
      medium: [],
      high: [],
    };
    for (const entry of filteredCodes) {
      if (entry.riskLevel === 'prohibited') continue; // Don't show prohibited in selector
      const group = groups[entry.riskLevel];
      if (group) group.push(entry);
    }
    return groups;
  }, [filteredCodes]);

  const selectedEntry = value
    ? MCC_CODES.find((e) => e.code === value) ?? null
    : null;

  const handleSelect = useCallback(
    (code: string) => {
      onChange(code);
      setIsOpen(false);
      setSearch('');
    },
    [onChange],
  );

  return (
    <div className="relative w-full">
      <label className="mb-1 block text-sm font-medium text-ink-muted">
        {t('mccSelector.label')}
      </label>

      {/* Selected value display / trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={[
          'flex w-full items-center justify-between rounded-lg border px-4 py-2.5 text-start text-sm transition-colors',
          isOpen ? 'border-[var(--nv-primary-color)] ring-1 ring-[var(--nv-primary-color)]' : 'border-hairline-strong',
          disabled ? 'cursor-not-allowed bg-surface-sunken opacity-50' : 'bg-white hover:border-hairline-strong',
        ].join(' ')}
      >
        {selectedEntry ? (
          <span>
            <span className="font-mono font-medium">{selectedEntry.code}</span>
            <span className="ms-2 text-ink-muted">{selectedEntry.description}</span>
          </span>
        ) : (
          <span className="text-ink-muted">{t('mccSelector.placeholder')}</span>
        )}
        <svg
          className={`ms-2 h-4 w-4 text-ink-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-hairline bg-white shadow-lg">
          {/* Search input */}
          <div className="border-b border-hairline px-3 py-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('mccSelector.searchPlaceholder')}
              className="w-full rounded border border-hairline px-3 py-1.5 text-sm focus:border-[var(--nv-primary-color)] focus:outline-none focus:ring-1 focus:ring-[var(--nv-primary-color)]"
              autoFocus
            />
          </div>

          {/* Results */}
          <div className="max-h-64 overflow-y-auto p-2">
            {filteredCodes.length === 0 && (
              <p className="px-3 py-4 text-center text-sm text-ink-muted">
                {t('mccSelector.noResults')}
              </p>
            )}

            {(['low', 'medium', 'high'] as const).map((level) => {
              const entries = grouped[level];
              if (!entries || entries.length === 0) return null;
              return (
                <div key={level} className="mb-2">
                  <p className="px-2 py-1 text-xs font-semibold uppercase text-ink-muted">
                    {riskLabel(level)}{t('mccSelector.riskSuffix')}
                  </p>
                  {entries.map((entry) => (
                    <button
                      key={entry.code}
                      type="button"
                      onClick={() => handleSelect(entry.code)}
                      className={[
                        'flex w-full items-center gap-2 rounded px-2 py-1.5 text-start text-sm hover:bg-surface-sunken',
                        value === entry.code ? 'bg-primary-tint' : '',
                      ].join(' ')}
                    >
                      <span className="shrink-0 font-mono font-medium text-ink">
                        {entry.code}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-ink-muted">
                        {entry.description}
                      </span>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${RISK_COLORS[entry.riskLevel]}`}
                      >
                        {riskLabel(entry.riskLevel)}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Risk level indicator for selected code */}
      {selectedEntry && (
        <div className="mt-1 flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${RISK_COLORS[selectedEntry.riskLevel]}`}
          >
            {riskLabel(selectedEntry.riskLevel)}{t('mccSelector.riskSuffix')}
          </span>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-1 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
